import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, CheckCircle, AlertTriangle, MessageSquare, X, CheckCheck } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

const statusIcons = {
  approved: { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50" },
  needs_change: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  comment: { icon: MessageSquare, color: "text-indigo-500", bg: "bg-indigo-50" },
};

const statusLabels = {
  approved: "Approuvé",
  needs_change: "À modifier",
  comment: "Commentaire",
};

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "a l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/count`, { withCredentials: true });
      setUnreadCount(data.count);
    } catch {
      // Silent
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data);
    } catch {
      // Silent
    }
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      // Silent
    }
  };

  // Poll for new notifications every 30s
  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [loadCount]);

  // Load full list when panel opens
  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  return (
    <div className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell size={17} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute inset-0 sm:inset-auto sm:right-0 sm:top-11 bg-white sm:border sm:border-gray-200 sm:rounded-xl sm:shadow-2xl sm:w-80 z-[100] overflow-hidden flex flex-col sm:max-h-[70vh]" data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h4 className="text-xs font-bold text-gray-900">Notifications</h4>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[9px] font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-indigo-50 transition-colors"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck size={10} /> Tout lire
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-400">
                <X size={13} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 && (
              <div className="py-10 text-center">
                <Bell size={24} className="mx-auto text-gray-200 mb-2" />
                <p className="text-xs text-gray-400">Aucune notification</p>
              </div>
            )}
            {notifications.map((n) => {
              const st = statusIcons[n.status] || statusIcons.comment;
              const Icon = st.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => {
                    setOpen(false);
                    navigate(`/editor/${n.diagram_id}`);
                  }}
                  className={`w-full px-4 py-3 text-left flex gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                    !n.read ? "bg-indigo-50/30" : ""
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${st.bg} flex items-center justify-center shrink-0`}>
                    <Icon size={14} className={st.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-gray-900">{n.author_name}</span>
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${
                        n.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                        n.status === "needs_change" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {statusLabels[n.status] || "Commentaire"}
                      </span>
                      {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                    </div>
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">{n.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-gray-400">{n.diagram_title}</span>
                      <span className="text-[8px] text-gray-300">{timeAgo(n.created_at)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
